// Brief end-of-call toast — appears top-center, auto-clears after ~2.5s
// (callManager schedules the transition to 'idle'). Click anywhere on the
// toast to dismiss immediately. Per-reason headline copy; state.message is
// surfaced as a subtitle only when it adds detail beyond the headline (the
// 'failed' branch carries the underlying error, for example).
//
// No name or duration is shown — neither lives in state.kind === 'ended'
// (it carries reason + message only), and the dispatch's anti-fabrication
// rule says don't invent fields the manager doesn't expose. If/when those
// land in CallState, drop them in here.

import { Phone, PhoneOff, PhoneMissed, AlertCircle } from 'lucide-react'
import type { ComponentType } from 'react'
import { useCallManager } from '@/lib/callManager'
import { formatDuration } from './formatDuration'

type Reason = 'declined' | 'missed' | 'canceled' | 'ended' | 'failed'
type Tone = 'neutral' | 'danger'

const HEADLINE: Record<Reason, string> = {
  ended: 'Call ended',
  declined: 'Call declined',
  missed: 'No answer',
  canceled: 'Call canceled',
  failed: 'Call failed',
}

const ICON: Record<Reason, ComponentType<{ style?: React.CSSProperties }>> = {
  ended: Phone,
  declined: PhoneOff,
  missed: PhoneMissed,
  canceled: PhoneOff,
  failed: AlertCircle,
}

const TONE: Record<Reason, Tone> = {
  ended: 'neutral',
  declined: 'danger',
  missed: 'danger',
  canceled: 'neutral',
  failed: 'danger',
}

export function CallEndedToast() {
  const { state, dismissEnded } = useCallManager()
  if (state.kind !== 'ended') return null

  const reason = state.reason as Reason
  const baseHeadline = HEADLINE[reason]
  const Icon = ICON[reason]
  const tone = TONE[reason]

  // Append the duration to the headline ONLY for 'ended' (the answered-
  // then-hung-up reason). The other four reasons never connected, so
  // duration is undefined for them and they render unchanged.
  const headline =
    reason === 'ended' && state.durationSec != null
      ? `${baseHeadline} · ${formatDuration(state.durationSec)}`
      : baseHeadline

  // Subtitle only when state.message adds info beyond the BASE headline
  // (e.g. failed → contains the actual error). Compared against the base
  // so the duration suffix doesn't accidentally suppress a useful message.
  const subtitle =
    state.message && state.message !== baseHeadline ? state.message : null

  const iconBg   = tone === 'danger' ? 'var(--danger-dim)'    : 'var(--surface-raised)'
  const iconInk  = tone === 'danger' ? 'var(--danger)'        : 'var(--ink-secondary)'
  const iconRing = tone === 'danger' ? 'var(--danger-border)' : 'var(--border-subtle)'

  return (
    <div
      className="call-toast-enter"
      role="status"
      aria-live="polite"
      onClick={dismissEnded}
      style={{
        position: 'fixed', top: 24, left: '50%',
        transform: 'translateX(-50%)',
        background: 'var(--surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 14, padding: '12px 16px',
        boxShadow: 'var(--shadow-popover-mid)',
        zIndex: 500, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 12,
        maxWidth: 'min(420px, calc(100vw - 32px))',
      }}
    >
      {/* Status icon circle */}
      <div
        aria-hidden="true"
        style={{
          width: 32, height: 32, borderRadius: '50%',
          background: iconBg, color: iconInk,
          border: `1px solid ${iconRing}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: 16, height: 16 }} />
      </div>

      {/* Text */}
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{
          fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)',
          letterSpacing: '-0.01em',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {headline}
        </span>
        {subtitle && (
          <span style={{
            fontSize: 12, color: 'var(--ink-tertiary)',
            marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {subtitle}
          </span>
        )}
      </div>
    </div>
  )
}
